import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './bloglist.scss';

import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';

// ─── MUI Icons ─────────────────────────────────────────────────────────────
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TuneIcon from '@mui/icons-material/Tune';

// ─── Constants ─────────────────────────────────────────────────────────────
const CATEGORIES = [
    'Culture et patrimoine du Maroc',
    'Shopping au Maroc',
    'Gastronomie marocaine',
    'Artisanat marocain',
    'Voyage et aventure',
    'Événements',
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'Tous' },
    { value: 'published', label: 'Publiés' },
    { value: 'draft', label: 'Brouillons' },
];

// ─── Signature motif ────────────────────────────────────────────────────────
// An 8-point "khatam" star, drawn from two overlapping squares — the same
// construction used in Moroccan zellige tilework — rendered as restrained
// line art. This single mark carries the whole page's identity: it shows up
// small (view toggle, featured flag), large (empty state) and as a faint
// oversized watermark behind the masthead.
const ZelligeStar = ({ className = '', filled = false }) => (
    <svg className={`zellige-mark ${className}`} viewBox="0 0 100 100" aria-hidden="true">
        <g fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="4">
            <rect x="24" y="24" width="52" height="52" />
            <rect x="24" y="24" width="52" height="52" transform="rotate(45 50 50)" />
        </g>
    </svg>
);

const ZelligeDivider = () => (
    <svg className="zellige-divider" viewBox="0 0 640 20" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
            <pattern id="zellige-motif" width="40" height="20" patternUnits="userSpaceOnUse">
                <line x1="0" y1="10" x2="12" y2="10" stroke="currentColor" strokeWidth="1" />
                <rect x="14" y="4" width="12" height="12" stroke="currentColor" strokeWidth="1" fill="none" />
                <rect x="14" y="4" width="12" height="12" stroke="currentColor" strokeWidth="1" fill="none" transform="rotate(45 20 10)" />
                <line x1="28" y1="10" x2="40" y2="10" stroke="currentColor" strokeWidth="1" />
            </pattern>
        </defs>
        <rect width="640" height="20" fill="url(#zellige-motif)" />
    </svg>
);

const VIEW_MODE_KEY = 'moroccool_blog_view_mode';

// ─── Component ─────────────────────────────────────────────────────────────
const BlogList = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ── Filters ──
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 10;

    // ── UI state ──
    const [collapsed, setCollapsed] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem(VIEW_MODE_KEY) || 'grid';
        } catch {
            return 'grid';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(VIEW_MODE_KEY, viewMode);
        } catch {
            /* ignore */
        }
    }, [viewMode]);

    // ── Fetch blogs ──
    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: currentPage, limit });

            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (categoryFilter !== 'all') params.append('category', categoryFilter);
            if (searchTerm.trim()) params.append('search', searchTerm.trim());
            if (sortBy === 'oldest') params.append('sort', 'oldest');

            const res = await axios.get(`https://api.moroccool.com/api/blogs?${params}`);
            setBlogs(res.data.blogs || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalItems(res.data.total || 0);
        } catch (err) {
            console.error('Error fetching blogs:', err);
            setError('Impossible de charger les articles.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter, categoryFilter, searchTerm, sortBy]);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    // ── Derived stats for the masthead ──
    const publishedOnPage = useMemo(
        () => blogs.filter((b) => b.status === 'published').length,
        [blogs]
    );
    const activeFilterCount =
        (statusFilter !== 'all' ? 1 : 0) +
        (categoryFilter !== 'all' ? 1 : 0) +
        (searchTerm.trim() ? 1 : 0);

    // ── Handlers ──
    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cet article définitivement ?')) return;
        try {
            await axios.delete(`https://api.moroccool.com/api/blogs/${id}`);
            fetchBlogs();
        } catch (err) {
            alert('Erreur lors de la suppression.');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Supprimer ${selectedIds.length} article(s) ?`)) return;
        try {
            await Promise.all(selectedIds.map((id) => axios.delete(`https://api.moroccool.com/api/blogs/${id}`)));
            setSelectedIds([]);
            fetchBlogs();
        } catch (err) {
            alert('Erreur lors de la suppression en masse.');
        }
    };

    const toggleSelect = (e, id) => {
        e.stopPropagation();
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === blogs.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(blogs.map((b) => b._id));
        }
    };

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

    const getStatusBadge = (status) =>
        status === 'published' ? (
            <span className="status-badge is-published">Publié</span>
        ) : (
            <span className="status-badge is-draft">Brouillon</span>
        );

    // ── Render ──
    return (
        <div className="blog-admin">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            <div className="blog-main">
                <Navbar />

                {/* ─── MASTHEAD ─────────────────────────────────────────────── */}
                <header className="masthead">

                    <div className="masthead-row">
                        <div className="masthead-heading">
                            <p className="masthead-eyebrow">Éditorial — Moroccool</p>
                            <h1 className="masthead-title">Blog</h1>
                            <p className="masthead-stats">
                                <span>{totalItems} article{totalItems !== 1 ? 's' : ''}</span>
                                {blogs.length > 0 && (
                                    <>
                                        <span className="masthead-stats-dot" />
                                        <span>{publishedOnPage} publié{publishedOnPage !== 1 ? 's' : ''} sur cette page</span>
                                    </>
                                )}
                            </p>
                        </div>
                        <button className="btn-primary" onClick={() => navigate('/blogs/new')}>
                            <AddIcon /> Nouvel article
                        </button>
                    </div>
                </header>

                <div className="blog-content">
                    {/* ─── TOOLBAR ─────────────────────────────────────────────── */}
                    <div className="toolbar">
                        <div className="search-field">
                            <SearchIcon className="search-field-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher un article…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-field-input"
                            />
                            {searchTerm && (
                                <button className="search-field-clear" onClick={() => setSearchTerm('')}>
                                    <CloseIcon />
                                </button>
                            )}
                        </div>

                        <button
                            className={`toolbar-btn ${showFilters ? 'is-active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <TuneIcon />
                            Filtres
                            {activeFilterCount > 0 && <span className="toolbar-btn-count">{activeFilterCount}</span>}
                        </button>

                        <div className="view-toggle" role="group" aria-label="Mode d'affichage">
                            <button
                                className={viewMode === 'grid' ? 'is-active' : ''}
                                onClick={() => setViewMode('grid')}
                                aria-label="Vue grille"
                                title="Vue grille"
                            >
                                <GridViewOutlinedIcon />
                            </button>
                            <button
                                className={viewMode === 'list' ? 'is-active' : ''}
                                onClick={() => setViewMode('list')}
                                aria-label="Vue liste"
                                title="Vue liste"
                            >
                                <ViewListOutlinedIcon />
                            </button>
                        </div>
                    </div>

                    {showFilters && (
                        <div className="filters-panel">
                            <div className="filter-group">
                                <label>Statut</label>
                                <div className="pill-group">
                                    {STATUS_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            className={statusFilter === opt.value ? 'is-active' : ''}
                                            onClick={() => setStatusFilter(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="filter-group">
                                <label>Catégorie</label>
                                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                    <option value="all">Toutes les catégories</option>
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Trier par</label>
                                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                    <option value="newest">Plus récent</option>
                                    <option value="oldest">Plus ancien</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* ─── STATES ──────────────────────────────────────────────── */}
                    {error && <div className="state-error">{error}</div>}

                    {loading ? (
                        <div className="state-loading">
                            <div className="state-loading-spinner" />
                            <p>Chargement…</p>
                        </div>
                    ) : blogs.length === 0 ? (
                        <div className="state-empty">

                            <p>Aucun article pour l'instant.</p>
                            <span>Les articles publiés et brouillons apparaîtront ici.</span>
                            <button className="btn-primary" onClick={() => navigate('/blogs/new')}>
                                <AddIcon /> Créer votre premier article
                            </button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        /* ─── GRID VIEW ───────────────────────────────────────── */
                        <div className="blog-grid">
                            {blogs.map((blog) => {
                                const selected = selectedIds.includes(blog._id);
                                return (
                                    <article
                                        key={blog._id}
                                        className={`blog-card ${selected ? 'is-selected' : ''}`}
                                        onClick={() => navigate(`/blogs/${blog._id}`)}
                                    >
                                        <div className="blog-card-media">
                                            <img src={blog.heroImage} alt={blog.title} loading="lazy" />
                                            <button
                                                className={`blog-card-check ${selected ? 'is-checked' : ''}`}
                                                onClick={(e) => toggleSelect(e, blog._id)}
                                                aria-label="Sélectionner"
                                            >
                                                {selected && <span />}
                                            </button>
                                            {blog.featured && (
                                                <span className="blog-card-featured" title="À la une">
                                                    <ZelligeStar filled />
                                                </span>
                                            )}
                                            <div className="blog-card-scrim">
                                                <button
                                                    className="scrim-btn"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/blogs/${blog._id}`); }}
                                                    title="Modifier"
                                                >
                                                    <EditIcon />
                                                </button>
                                                <button
                                                    className="scrim-btn"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/blogs/view/${blog._id}`); }}
                                                    title="Voir"
                                                >
                                                    <VisibilityIcon />
                                                </button>
                                                <button
                                                    className="scrim-btn is-danger"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(blog._id); }}
                                                    title="Supprimer"
                                                >
                                                    <DeleteOutlineIcon />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="blog-card-body">
                                            <div className="blog-card-tags">
                                                <span className="category-tag">{blog.category || 'Non catégorisé'}</span>
                                                {getStatusBadge(blog.status)}
                                            </div>
                                            <h3 className="blog-card-title">{blog.title}</h3>
                                            <p className="blog-card-excerpt">
                                                {blog.subtitle || blog.excerpt?.slice(0, 90) || ''}
                                            </p>
                                            <span className="blog-card-date">{formatDate(blog.createdAt)}</span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        /* ─── LIST VIEW ───────────────────────────────────────── */
                        <div className="blog-list-wrap">
                            <div className="blog-list-head">
                                <span className="list-th-check">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length === blogs.length && blogs.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </span>
                                <span className="list-th-title">Titre</span>
                                <span className="list-th-category">Catégorie</span>
                                <span className="list-th-status">Statut</span>
                                <span className="list-th-date">Date</span>
                                <span className="list-th-actions">Actions</span>
                            </div>
                            {blogs.map((blog) => {
                                const selected = selectedIds.includes(blog._id);
                                return (
                                    <div
                                        key={blog._id}
                                        className={`blog-list-row ${selected ? 'is-selected' : ''}`}
                                        onClick={() => navigate(`/blogs/${blog._id}`)}
                                    >
                                        <span className="list-td-check" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected} onChange={(e) => toggleSelect(e, blog._id)} />
                                        </span>
                                        <span className="list-td-title">
                                            <img src={blog.heroImage} alt={blog.title} />
                                            <span className="list-title-block">
                                                <span className="list-title-text">
                                                    {blog.title}
                                                    {blog.featured && <ZelligeStar className="list-featured" filled />}
                                                </span>
                                                <span className="list-title-sub">
                                                    {blog.subtitle || blog.excerpt?.slice(0, 60) || ''}
                                                </span>
                                            </span>
                                        </span>
                                        <span className="list-td-category">{blog.category || 'Non catégorisé'}</span>
                                        <span className="list-td-status">{getStatusBadge(blog.status)}</span>
                                        <span className="list-td-date">{formatDate(blog.createdAt)}</span>
                                        <span className="list-td-actions" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => navigate(`/blogs/${blog._id}`)} title="Modifier"><EditIcon /></button>
                                            <button onClick={() => handleDelete(blog._id)} title="Supprimer"><DeleteOutlineIcon /></button>
                                            <button onClick={() => navigate(`/blogs/view/${blog._id}`)} title="Voir"><VisibilityIcon /></button>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ─── PAGINATION ──────────────────────────────────────────── */}
                    {!loading && blogs.length > 0 && totalPages > 1 && (
                        <div className="pager">
                            <button
                                className="pager-arrow"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                aria-label="Page précédente"
                            >
                                <ArrowBackIosNewIcon />
                            </button>

                            <div className="pager-dots">
                                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 7) pageNum = i + 1;
                                    else if (currentPage <= 4) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                                    else pageNum = currentPage - 3 + i;
                                    const active = currentPage === pageNum;
                                    return (
                                        <button
                                            key={pageNum}
                                            className={active ? 'is-active' : ''}
                                            onClick={() => setCurrentPage(pageNum)}
                                        >
                                            {active && <ZelligeStar className="pager-mark" />}
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                className="pager-arrow"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                aria-label="Page suivante"
                            >
                                <ArrowForwardIosIcon />
                            </button>
                        </div>
                    )}

                    {!loading && blogs.length > 0 && (
                        <div className="results-footnote">{blogs.length} sur {totalItems} articles</div>
                    )}
                </div>

                <footer className="blog-footer">
                    © {new Date().getFullYear()} <span>Moroccool</span> — Tous droits réservés.
                </footer>
            </div>

            {/* ─── FLOATING BULK TRAY ────────────────────────────────────────── */}
            {selectedIds.length > 0 && (
                <div className="bulk-tray">
                    <span className="bulk-tray-count">{selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
                    <span className="bulk-tray-sep" />
                    <button className="bulk-tray-delete" onClick={handleBulkDelete}>
                        <DeleteOutlineIcon /> Supprimer
                    </button>
                    <button className="bulk-tray-clear" onClick={() => setSelectedIds([])}>
                        <CloseIcon />
                    </button>
                </div>
            )}
        </div>
    );
};

export default BlogList;